import { ComboBox, CreateNewOption, LoadMoreOption, LoadingOption, NoResultsOption, Option, TwoLineOption, } from '@routable/gross-ds';
import { EventKeyValues } from '@routable/shared';
import React, { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
export const ReduxFormGrossDsComboBox = React.memo(({ components = { Option: TwoLineOption }, createLabel, input, isCreatable, loadingText = 'Loading...', options, onInputChange, fetchMore, hasMore, isFetching, fieldVal = '', ...props }) => {
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setFocus] = React.useState(false);
    const [valueJustSelected, setValueJustSelected] = useState(false);
    useEffect(() => {
        setInputValue(fieldVal);
    }, [fieldVal]);
    useEffect(() => {
        if (!isFocused && fieldVal !== inputValue) {
            setInputValue(fieldVal);
        }
    }, [isFocused]);
    useEffect(() => {
        onInputChange?.(inputValue);
    }, [inputValue]);
    const optionList = useMemo(() => [
        ...(isCreatable && inputValue
            ? [
                {
                    id: 'create_new_option',
                    key: 'create_new_option',
                    value: 'create_new_option',
                    children: React.createElement(CreateNewOption, { createLabel: createLabel, inputValue: inputValue }),
                },
                {
                    id: 'divider',
                    key: 'divider',
                    value: 'divider',
                    children: React.createElement("div", { className: "form-divider thin w-full !m-0" }),
                },
            ]
            : []),
        ...options.map((option) => ({
            ...option,
            children: (React.createElement(components.Option, { data: option, description: option.description, disabled: option.isDisabled, label: option.label })),
        })),
        isFetching && {
            id: 'loading',
            key: 'loading',
            label: 'Loading',
            value: 'Loading',
            children: React.createElement(LoadingOption, null, loadingText),
        },
        !isFetching &&
            !inputValue &&
            options.length === 0 && {
            id: 'type-to-search',
            key: 'type-to-search',
            label: 'Type to search',
            value: 'type-to-search',
            children: React.createElement("div", { className: "!text-grey-60 leading-6" }, "Type to search"),
        },
        !isFetching &&
            inputValue &&
            options.length === 0 && {
            id: 'no-results-found',
            key: 'no-results-found',
            label: 'No results found',
            value: 'No results found',
            children: React.createElement(NoResultsOption, null),
        },
        hasMore &&
            fetchMore &&
            !isFetching && {
            id: 'load-more',
            key: 'load-more',
            label: 'Load More',
            value: 'Load More',
            children: React.createElement(LoadMoreOption, { onClick: fetchMore }),
        },
    ].filter(Boolean), [fetchMore, hasMore, inputValue, isCreatable, isFetching, options]);
    const disabledKeys = useMemo(() => [
        'loading',
        'divider',
        'type-to-search',
        'no-results-found',
        ...(isFetching ? ['load-more'] : []),
        ...optionList.filter((opt) => opt.isDisabled).map((opt) => opt.value),
    ], [isFetching, optionList]);
    return (React.createElement(ComboBox, { ...props, ...input, disabledKeys: disabledKeys, inputValue: inputValue, isValueControlled: true, onBlur: () => {
            setFocus(false);
        }, onKeyUp: (e) => {
            if (valueJustSelected) {
                setValueJustSelected(false);
                return;
            }
            if (e.key === EventKeyValues.ENTER && isCreatable) {
                if (!inputValue) {
                    props.onClear();
                }
                else {
                    input.onChange(props.getNewOptData(inputValue));
                }
            }
        }, onChange: (key) => {
            if (!key) {
                return;
            }
            setValueJustSelected(true);
            if (key === 'create_new_option') {
                input.onChange(props.getNewOptData(inputValue));
                return;
            }
            const option = Array.from(options).find((opt) => opt.value === key);
            input.onChange(option);
        }, onFocus: () => {
            setFocus(true);
        }, onInputChange: setInputValue, useTriggerAsPortalContainer: true, value: fieldVal }, optionList.map(({ label, value: val, ...rest }) => (React.createElement(Option, { key: val, textValue: label, ...rest })))));
}, isEqual);
